import type { PickItem, PickList } from '../types';

export interface TemperatureZone {
  id: string;
  name: string;
  type: 'frozen' | 'chilled' | 'ambient';
  minTemp: number;
  maxTemp: number;
  alertThresholds: {
    warning: number;
    critical: number;
  };
  requiredEquipment?: string[];
  maxPickTime?: number; // minutes
  pickingOrder: number;
}

export interface TemperatureReading {
  zoneId: string;
  temperature: number;
  humidity?: number;
  timestamp: string;
  sensorId: string;
  status: 'normal' | 'warning' | 'critical';
}

export interface ColdChainEvent {
  id: string;
  type: 'temperature_breach' | 'zone_entry' | 'zone_exit' | 'equipment_failure';
  zoneId: string;
  pickerId?: string;
  orderId?: string;
  temperature?: number;
  duration?: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
  timestamp: string;
  resolved: boolean;
  notes?: string;
}

export class TemperatureZoneService {
  private zones: Map<string, TemperatureZone> = new Map();
  private currentReadings: Map<string, TemperatureReading> = new Map();
  private events: ColdChainEvent[] = [];

  constructor() {
    this.initializeDefaultZones();
  }

  private initializeDefaultZones(): void {
    const defaultZones: TemperatureZone[] = [
      {
        id: 'frozen',
        name: 'Frozen Storage',
        type: 'frozen',
        minTemp: -25,
        maxTemp: -18,
        alertThresholds: { warning: -15, critical: -10 },
        requiredEquipment: ['insulated_gloves', 'thermal_jacket'],
        maxPickTime: 15,
        pickingOrder: 3
      },
      {
        id: 'chilled',
        name: 'Chilled Storage',
        type: 'chilled',
        minTemp: 0,
        maxTemp: 4,
        alertThresholds: { warning: 6, critical: 8 },
        requiredEquipment: ['light_jacket'],
        maxPickTime: 30,
        pickingOrder: 2
      },
      {
        id: 'ambient',
        name: 'Ambient Storage',
        type: 'ambient',
        minTemp: 15,
        maxTemp: 25,
        alertThresholds: { warning: 28, critical: 32 },
        maxPickTime: 60,
        pickingOrder: 1
      }
    ];

    defaultZones.forEach(zone => this.zones.set(zone.id, zone));
  }

  getZone(zoneId: string): TemperatureZone | undefined {
    return this.zones.get(zoneId);
  }

  getAllZones(): TemperatureZone[] {
    return Array.from(this.zones.values()).sort((a, b) => a.pickingOrder - b.pickingOrder);
  }

  addTemperatureReading(reading: Omit<TemperatureReading, 'status'>): void {
    const zone = this.zones.get(reading.zoneId);
    if (!zone) return;

    const status = this.determineStatus(reading.temperature, zone);
    const fullReading: TemperatureReading = { ...reading, status };
    
    this.currentReadings.set(reading.zoneId, fullReading);

    // Check for temperature breaches
    if (status === 'warning' || status === 'critical') {
      this.createTemperatureEvent(reading, zone, status);
    }
  }

  getCurrentReading(zoneId: string): TemperatureReading | undefined {
    return this.currentReadings.get(zoneId);
  }

  getAllCurrentReadings(): TemperatureReading[] {
    return Array.from(this.currentReadings.values());
  }

  optimizePickingRoute(pickList: PickList, items: PickItem[]): PickItem[] {
    // Group items by temperature zone
    const itemsByZone = this.groupItemsByZone(items);
    
    // Sort zones by picking order (ambient first, frozen last)
    const sortedZones = this.getAllZones().sort((a, b) => a.pickingOrder - b.pickingOrder);
    
    // Create optimized route
    const optimizedItems: PickItem[] = [];
    
    for (const zone of sortedZones) {
      const zoneItems = itemsByZone.get(zone.type) || [];
      if (zoneItems.length > 0) {
        // Sort items within zone by bin location
        const sortedZoneItems = zoneItems.sort((a, b) => 
          a.binLocation.localeCompare(b.binLocation)
        );
        optimizedItems.push(...sortedZoneItems);
      }
    }

    return optimizedItems;
  }

  validateColdChainCompliance(items: PickItem[]): {
    compliant: boolean;
    violations: string[];
    recommendations: string[];
  } {
    const violations: string[] = [];
    const recommendations: string[] = [];

    // Check for mixed temperature zones in same pick
    const zones = new Set(items.map(item => item.temperatureZone).filter(Boolean));
    
    if (zones.has('frozen') && zones.has('ambient')) {
      violations.push('Frozen and ambient items should not be picked together');
      recommendations.push('Split pick list by temperature zones');
    }

    // Check for expired items
    const expiredItems = items.filter(item => {
      if (!item.expiryDate) return false;
      return new Date(item.expiryDate) < new Date();
    });

    if (expiredItems.length > 0) {
      violations.push(`${expiredItems.length} expired items found`);
      recommendations.push('Remove expired items from pick list');
    }

    // Check current zone temperatures
    for (const zone of zones) {
      if (!zone) continue;
      
      const reading = this.getCurrentReading(zone);
      const zoneConfig = this.getZone(zone);
      
      if (reading && zoneConfig && reading.status !== 'normal') {
        violations.push(`${zoneConfig.name} temperature out of range: ${reading.temperature}°C`);
        recommendations.push(`Wait for ${zoneConfig.name} temperature to normalize`);
      }
    }

    return {
      compliant: violations.length === 0,
      violations,
      recommendations
    };
  }

  createZoneEntryEvent(zoneId: string, pickerId: string, orderId?: string): void {
    const event: ColdChainEvent = {
      id: `event-${Date.now()}`,
      type: 'zone_entry',
      zoneId,
      pickerId,
      orderId,
      severity: 'low',
      timestamp: new Date().toISOString(),
      resolved: true
    };

    this.events.push(event);
  }

  createZoneExitEvent(zoneId: string, pickerId: string, orderId?: string): void {
    const event: ColdChainEvent = {
      id: `event-${Date.now()}`,
      type: 'zone_exit',
      zoneId,
      pickerId,
      orderId,
      severity: 'low',
      timestamp: new Date().toISOString(),
      resolved: true
    };

    this.events.push(event);
  }

  getColdChainEvents(filters?: {
    zoneId?: string;
    pickerId?: string;
    severity?: string;
    resolved?: boolean;
  }): ColdChainEvent[] {
    let filteredEvents = this.events;

    if (filters) {
      if (filters.zoneId) {
        filteredEvents = filteredEvents.filter(e => e.zoneId === filters.zoneId);
      }
      if (filters.pickerId) {
        filteredEvents = filteredEvents.filter(e => e.pickerId === filters.pickerId);
      }
      if (filters.severity) {
        filteredEvents = filteredEvents.filter(e => e.severity === filters.severity);
      }
      if (filters.resolved !== undefined) {
        filteredEvents = filteredEvents.filter(e => e.resolved === filters.resolved);
      }
    }

    return filteredEvents.sort((a, b) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }

  private groupItemsByZone(items: PickItem[]): Map<string, PickItem[]> {
    const grouped = new Map<string, PickItem[]>();
    
    for (const item of items) {
      const zone = item.temperatureZone || 'ambient';
      if (!grouped.has(zone)) {
        grouped.set(zone, []);
      }
      grouped.get(zone)!.push(item);
    }
    
    return grouped;
  }

  private determineStatus(temperature: number, zone: TemperatureZone): 'normal' | 'warning' | 'critical' {
    if (temperature >= zone.alertThresholds.critical || temperature <= zone.minTemp - 5) {
      return 'critical';
    }
    if (temperature >= zone.alertThresholds.warning || temperature <= zone.minTemp - 2) {
      return 'warning';
    }
    return 'normal';
  }

  private createTemperatureEvent(
    reading: Omit<TemperatureReading, 'status'>, 
    zone: TemperatureZone, 
    status: 'warning' | 'critical'
  ): void {
    const event: ColdChainEvent = {
      id: `temp-${Date.now()}`,
      type: 'temperature_breach',
      zoneId: reading.zoneId,
      temperature: reading.temperature,
      severity: status === 'critical' ? 'critical' : 'medium',
      timestamp: reading.timestamp,
      resolved: false,
      notes: `Temperature ${reading.temperature}°C outside acceptable range (${zone.minTemp}°C - ${zone.maxTemp}°C)`
    };

    this.events.push(event);
  }
}

export const temperatureZoneService = new TemperatureZoneService();